import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Star, Clock, ArrowRight } from 'lucide-react';
import Layout from '../components/Layout';
import Seo from '../components/Seo';
import LeadForm from '../components/LeadForm';
import { api } from '../lib/api';
import { categoryBySlug } from '../data/categories';
import { TREATMENTS } from '../data/treatments';
import { trackClinic } from '../lib/tracking';

// Map a treatment name (CATEGORIES.treatments) -> slug (TREATMENTS key) so each
// treatment in this category page links to its dedicated landing page.
const TREATMENT_NAME_TO_SLUG = Object.entries(TREATMENTS).reduce((acc, [slug, cfg]) => {
  acc[cfg.treatment] = slug;
  return acc;
}, {});

export default function CategoryPage() {
  const { kategori } = useParams();
  const cat = categoryBySlug(kategori);
  const [clinics, setClinics] = useState([]);

  useEffect(() => {
    api.get('/clinics/public').then((r) => setClinics(r.data.clinics || []));
  }, []);

  if (!cat) return <Layout><div className="p-16 text-center">Kategori ikke funnet</div></Layout>;

  const catClinics = clinics.filter((c) => c.categories.includes(cat.name));

  return (
    <Layout>
      <Seo
        title={`${cat.name} i Norge – Finn godkjente spesialister | Specialists.no`}
        description={`Finn og bli kontaktet av Norges beste ${cat.name.toLowerCase()}-klinikker. Kun 4+ stjerner. Gratis og uforpliktende.`}
        canonical={`https://specialists.no/behandlinger/${cat.slug}`}
      />
      <section className="bg-[#0A1F44] text-white py-14 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <nav className="text-sm text-white/50 mb-3">
            <Link to="/" className="hover:text-white">Hjem</Link> / <span className="text-white">{cat.name}</span>
          </nav>
          <h1 className="font-display text-4xl lg:text-5xl font-bold">{cat.name}</h1>
          <p className="text-white/70 mt-4 text-lg max-w-2xl">{cat.description}. Kun klinikker med 4+ stjerner og erfarne medisinske behandlere.</p>
        </div>
      </section>

      <section className="py-12 lg:py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-[1.2fr_1fr] gap-10">
          <div>
            <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-6">Partnerklinikker</h2>
            {catClinics.length === 0 ? (
              <div className="bg-white rounded-xl p-6 border border-[#0A1F44]/10 text-[#64748B] text-sm mb-12">
                Vi har ingen aktive partnerklinikker for {cat.name.toLowerCase()} ennå. Send en forespørsel — vi finner en kvalifisert behandler til deg.
              </div>
            ) : (
              <div className="grid sm:grid-cols-2 gap-4 mb-12">
                {catClinics.map((c) => (
                  <Link key={c.id} to={`/klinikker/${c.id}`} onClick={() => trackClinic(c.name, cat.name)} data-testid={`cat-clinic-${c.id}`} className="bg-white rounded-xl p-5 border border-[#0A1F44]/10 hover:border-[#0A1F44]/30 hover:-translate-y-0.5 transition-all">
                    <div className="flex items-center gap-1 mb-2">
                      <Star size={13} fill="#3B82F6" className="text-[#3B82F6]" />
                      <span className="font-semibold text-[#0A1F44]">{c.rating}</span>
                      <span className="text-xs text-[#64748B]">({c.reviews})</span>
                      {c.featured && <span className="ml-auto text-[10px] uppercase tracking-wider font-bold text-[#3B82F6]">Partner</span>}
                    </div>
                    <div className="font-display text-lg font-bold text-[#0A1F44] mb-0.5">{c.name}</div>
                    <div className="text-xs text-[#64748B] mb-2">{c.location}</div>
                    <div className="flex items-center gap-1.5 text-xs"><Clock size={11} className="text-[#3B82F6]" /> {c.waitTime}</div>
                    {c.treatments && c.treatments.length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-3">
                        {c.treatments.slice(0, 3).map((t) => (
                          <span key={t} className="text-[10px] uppercase tracking-wider bg-[#FAF8F5] text-[#0A1F44] px-2 py-1 rounded-full">{t}</span>
                        ))}
                      </div>
                    )}
                  </Link>
                ))}
              </div>
            )}

            <h2 className="font-display text-2xl font-bold text-[#0A1F44] mb-6">Behandlinger i denne kategorien</h2>
            <div className="grid sm:grid-cols-2 gap-4">
              {cat.treatments.map((t) => {
                const slug = TREATMENT_NAME_TO_SLUG[t];
                const offering = catClinics.filter((c) => c.treatments && c.treatments.includes(t));
                const card = (
                  <div className="bg-white rounded-xl p-5 border border-[#0A1F44]/10 hover:border-[#0A1F44]/30 hover:-translate-y-0.5 transition-all h-full flex flex-col">
                    <div className="font-display text-lg font-bold text-[#0A1F44] mb-1">{t}</div>
                    <div className="text-xs text-[#64748B] mb-3">
                      {offering.length > 0
                        ? `${offering.length} klinikk${offering.length === 1 ? '' : 'er'} tilbyr dette`
                        : 'Send forespørsel — vi matcher deg'}
                    </div>
                    {offering.length > 0 && (
                      <ul className="text-xs text-[#0A1F44]/80 space-y-1 mb-3">
                        {offering.slice(0, 3).map((c) => (
                          <li key={c.id} className="flex items-center gap-1.5">
                            <Star size={10} fill="#3B82F6" className="text-[#3B82F6]" /> {c.name} <span className="text-[#64748B]">({c.rating})</span>
                          </li>
                        ))}
                      </ul>
                    )}
                    <div className="mt-auto inline-flex items-center gap-1 text-sm text-[#3B82F6] font-semibold">
                      {slug ? 'Les mer & få tilbud' : 'Få tilbud'} <ArrowRight size={12} />
                    </div>
                  </div>
                );
                return slug ? (
                  <Link key={t} to={`/${slug}`} data-testid={`cat-treatment-${slug}`} className="block">{card}</Link>
                ) : (
                  <a key={t} href="#lead-form" data-testid={`cat-treatment-${t}`} className="block">{card}</a>
                );
              })}
            </div>
          </div>

          <div id="lead-form" className="lg:sticky lg:top-28 h-fit">
            <LeadForm initialCategory={cat.name} clinics={clinics} compact />
          </div>
        </div>
      </section>
    </Layout>
  );
}
